import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WorkspaceWithRole, UserRole } from 'types';
import storage from 'utils/storage';
import { STORAGE_KEYS } from 'utils/constants';

interface WorkspaceState {
  currentWorkspace: WorkspaceWithRole | null;
  currentRole: UserRole | null;
  workspaces: WorkspaceWithRole[];
}

const initialState: WorkspaceState = {
  currentWorkspace: null,
  currentRole: null,
  workspaces: [],
};

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    setCurrentWorkspace: (state, action: PayloadAction<WorkspaceWithRole>) => {
      state.currentWorkspace = action.payload;
      state.currentRole = action.payload.role;
      // Store in localStorage for API header and role persistence
      storage.set(STORAGE_KEYS.CURRENT_WORKSPACE, action.payload.id);
      storage.set(STORAGE_KEYS.CURRENT_ROLE, action.payload.role);
    },
    setWorkspaces: (state, action: PayloadAction<WorkspaceWithRole[]>) => {
      state.workspaces = action.payload;
      // If no current workspace selected but workspaces exist
      if (!state.currentWorkspace && action.payload.length > 0) {
        // Check localStorage for saved workspace preference
        const savedWorkspaceId = storage.get(STORAGE_KEYS.CURRENT_WORKSPACE);
        const savedWorkspace = savedWorkspaceId 
          ? action.payload.find(w => w.id === savedWorkspaceId)
          : null;
        
        // Use saved workspace if found, otherwise default to first
        const workspaceToSet = savedWorkspace || action.payload[0];
        state.currentWorkspace = workspaceToSet;
        state.currentRole = workspaceToSet.role;
        storage.set(STORAGE_KEYS.CURRENT_WORKSPACE, workspaceToSet.id);
        storage.set(STORAGE_KEYS.CURRENT_ROLE, workspaceToSet.role);
      }
    },
    addWorkspace: (state, action: PayloadAction<WorkspaceWithRole>) => {
      state.workspaces.push(action.payload);
    },
    updateWorkspace: (state, action: PayloadAction<WorkspaceWithRole>) => {
      const index = state.workspaces.findIndex((w) => w.id === action.payload.id);
      if (index !== -1) {
        state.workspaces[index] = action.payload;
      }
      if (state.currentWorkspace?.id === action.payload.id) {
        state.currentWorkspace = action.payload;
        state.currentRole = action.payload.role;
        storage.set(STORAGE_KEYS.CURRENT_ROLE, action.payload.role);
      }
    },
    removeWorkspace: (state, action: PayloadAction<string>) => {
      state.workspaces = state.workspaces.filter((w) => w.id !== action.payload);
      if (state.currentWorkspace?.id === action.payload) {
        state.currentWorkspace = null;
        state.currentRole = null;
        storage.remove(STORAGE_KEYS.CURRENT_WORKSPACE);
        storage.remove(STORAGE_KEYS.CURRENT_ROLE);
      }
    },
    clearWorkspaces: (state) => {
      state.currentWorkspace = null;
      state.currentRole = null;
      state.workspaces = [];
      storage.remove(STORAGE_KEYS.CURRENT_WORKSPACE);
      storage.remove(STORAGE_KEYS.CURRENT_ROLE);
    },
  },
});

export const {
  setCurrentWorkspace,
  setWorkspaces,
  addWorkspace,
  updateWorkspace,
  removeWorkspace,
  clearWorkspaces,
} = workspaceSlice.actions;
export default workspaceSlice.reducer;
